<script setup lang="ts">
import { ref, toRefs } from "vue";
import { z } from "zod";

import QuestionnaireResponsesChart from "@/components/QuestionnaireResponsesChart.vue";
import type { FilterCriteria } from "@/components/QuestionnaireResponsesFilter.vue";
import QuestionnaireResponsesFilter from "@/components/QuestionnaireResponsesFilter.vue";
import { useValidatedProps } from "@/composable";
import { questionnaireWithResponsesSchema } from "@/types";

const props = useValidatedProps(
  z.object({
    questionnairesWithResponses: z.array(questionnaireWithResponsesSchema),
    selectedQuestionnaireId: z.string().nullish(),
  }),
);
const { questionnairesWithResponses } = toRefs(props);
const { selectedQuestionnaireId } = props;

const answeredQuestionnaireIds = questionnairesWithResponses.value
  .filter((questionnaire) => questionnaire.responses.length > 0)
  .map((questionnaire) => questionnaire.id);

const hasOlderResponses = questionnairesWithResponses.value.some(
  (questionnaire) => questionnaire.hasOlderResponses,
);

const noResponsesNotice = (() => {
  if (answeredQuestionnaireIds.length > 0) {
    return undefined;
  }

  if (hasOlderResponses) {
    return "Der er ingen registreringer de seneste 24 måneder.";
  }

  if (questionnairesWithResponses.value.some(({ manual }) => manual)) {
    return "Organisationen anvender spørgeskemaerne, men data deles ikke via KIH-databasen. Derfor kan der ikke vises registreringer.";
  }

  return undefined;
})();

const filterCriteria = ref<FilterCriteria>({
  questionnaireIds:
    selectedQuestionnaireId &&
    answeredQuestionnaireIds.includes(selectedQuestionnaireId)
      ? [selectedQuestionnaireId]
      : answeredQuestionnaireIds,
});
</script>

<template>
  <div class="d-flex flex-column flex-md-row p-3 gap-3">
    <aside class="p-1" style="max-width: 300px">
      <QuestionnaireResponsesFilter
        v-model="filterCriteria"
        :questionnaires="questionnairesWithResponses"
      />
    </aside>
    <main class="flex-grow-1 min-w-0">
      <div v-if="noResponsesNotice" class="alert alert-info">
        {{ noResponsesNotice }}
      </div>
      <QuestionnaireResponsesChart
        v-else
        :filter="filterCriteria"
        :questionnaires="questionnairesWithResponses"
      />
    </main>
  </div>
</template>
